import { useState, useCallback } from 'react';
import type { University, UniversityFormData } from '../types';
import * as universityService from '../services/universityService';

interface UseUniversitiesReturn {
  universities: University[];
  loading: boolean;
  showModal: boolean;
  editingUniversity: University | null;
  error: string;
  loadUniversities: () => Promise<University[]>;
  handleAdd: (data: UniversityFormData) => Promise<void>;
  handleEdit: (data: UniversityFormData) => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
  openModal: (university?: University) => void;
  closeModal: () => void;
}

export const useUniversities = (): UseUniversitiesReturn => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState<University | null>(null);
  const [error, setError] = useState('');

  const loadUniversities = useCallback(async () => {
    try {
      setLoading(true);
      const data = await universityService.getUniversities();
      setUniversities(data);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to load universities.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAdd = async (data: UniversityFormData) => {
    const added = await universityService.addUniversity(data);
    setUniversities(prev => [...prev, added]);
  };

  const handleEdit = async (data: UniversityFormData) => {
    if (!editingUniversity) throw new Error('No university selected for editing');
    await universityService.updateUniversity(editingUniversity.id, data);
    setUniversities(prev =>
      prev.map(uni => (uni.id === editingUniversity.id ? { ...uni, ...data } : uni))
    );
  };

  const handleDelete = async (id: string) => {
    await universityService.deleteUniversity(id);
    setUniversities(prev => prev.filter(uni => uni.id !== id));
  };

  const openModal = (university?: University) => {
    setEditingUniversity(university || null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUniversity(null);
  };

  return {
    universities,
    loading,
    showModal,
    editingUniversity,
    error,
    loadUniversities,
    handleAdd,
    handleEdit,
    handleDelete,
    openModal,
    closeModal,
  };
};
